import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CONFIG from "../config";
import BRANDING from "../branding";
import { Helmet } from "react-helmet-async";
import SEO from "../components/common/SEO";

const ResetPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isArabic = i18n.language?.startsWith('ar');

  const token = searchParams.get('token');

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const currentAppName = isArabic ? BRANDING.appName.ar : BRANDING.appName.en;
  const appInitials = currentAppName
    .trim()
    .split(/[\s-]+/)
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'F-S';

  useEffect(() => {
    if (!token) {
      setMessage(`❌ ${t('auth.invalid_token', 'Invalid or missing reset token.')}`);
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (password !== confirmPassword) {
      setMessage(`❌ ${t('auth.passwords_do_not_match', 'Passwords do not match')}`);
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const res = await axios.post(`${CONFIG.API_URL}/api/auth/reset-password`, { token, password });
      setMessage(`✅ ${res.data.message || t('auth.password_reset_success', 'Password reset successfully')}`);
      setIsSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || t('auth.error_occurred', 'An error occurred')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#fcfcfc]">
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>
          {isArabic
            ? `إعادة تعيين كلمة المرور | ${BRANDING.appName.ar}`
            : `Reset Password | ${BRANDING.appName.en}`}
        </title>
      </Helmet>
      <SEO />

      <div
        className="w-full max-w-5xl overflow-hidden flex flex-col md:flex-row shadow-2xl"
        style={{ backgroundColor: 'var(--bg-header)', borderRadius: 'var(--card-radius)', border: '1px solid var(--border)' }}
      >
        {/* Left Side - Visual/Branding */}
        <div className="md:w-1/2 bg-gray-900 relative p-12 flex flex-col justify-between text-white overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-primary/5 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"></div>

          <div className="relative z-10">
            <Link to="/" className="text-2xl font-black tracking-tighter hover:text-brand-primary transition-colors flex items-center gap-2">
              <span className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center text-white text-lg">{appInitials}</span>
              {isArabic ? BRANDING.appName.ar : BRANDING.appName.en}
            </Link>
          </div>

          <div className="relative z-10 mb-12">
            <h1 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
              {t('auth.secure_account', 'Secure Your Account')}
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed max-w-sm">
              {isArabic
                ? "قم بإنشاء كلمة مرور جديدة قوية لحماية حسابك."
                : "Create a new strong password to protect your account."
              }
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-4 text-sm text-gray-400">
            <span>
              {isArabic ? "أمانك هو أولويتنا" : "Your security is our priority"}
            </span>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="md:w-1/2 p-8 md:p-16 flex flex-col justify-center">
          <div className="max-w-md mx-auto w-full">
            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-black mb-2" style={{ color: 'var(--text-header)' }}>
                {t('auth.reset_password', 'Reset Password')}
              </h2>
              <p className="text-gray-400 font-medium">
                {t('auth.enter_new_password', 'Enter your new password below.')}
              </p>
            </div>

            {!isSuccess ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.new_password', 'New Password')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-lock absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="password"
                      name="password"
                      placeholder={t('auth.new_password_placeholder', 'Enter new password')}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={password}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold uppercase tracking-widest px-1" style={{ color: 'var(--text-header)' }}>
                    {t('auth.confirm_password', 'Confirm Password')}
                  </label>
                  <div className="relative">
                    <i className={`fas fa-check-circle absolute ${isArabic ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-gray-400`}></i>
                    <input
                      type="password"
                      name="confirmPassword"
                      placeholder={t('auth.confirm_password_placeholder', 'Confirm new password')}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full ${isArabic ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-4 outline-none transition-all font-medium`}
                      style={{ backgroundColor: 'var(--bg-body)', border: '1px solid var(--border)', borderRadius: 'var(--input-radius)' }}
                      value={confirmPassword}
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full bg-gray-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-brand-primary hover:shadow-xl hover:shadow-brand-primary/30 active:scale-[0.98] transition-all disabled:bg-gray-400 disabled:cursor-not-allowed transform"
                >
                  {loading ? t('auth.resetting', 'Resetting...') : t('auth.reset_password_btn', 'Reset Password')}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  <i className="fas fa-check"></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-header)' }}>{t('auth.password_reset', 'Password Reset')}</h3>
                <p className="text-gray-500 mb-6">{t('auth.redirecting_login', 'Redirecting you to login...')}</p>
              </div>
            )}

            {message && !isSuccess && (
              <div className={`mt-6 p-4 rounded-2xl text-center text-sm font-bold ${message.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
